import {rawRequest, responseError} from './http';
import {clearLegacyCredential, migrateLegacySession} from './legacySession';
import {withDeadline} from './requestDeadline';
import {requireSessionAcknowledgement} from './sessionProtocol';
export {REQUEST_HEADERS} from './http';

// Session credentials exist only in Secure HttpOnly cookies, never JS storage.
let ready = false;
let starting: Promise<void> | null = null;

async function confirmCookie(signal: AbortSignal): Promise<void> {
  const response = await rawRequest('/session', {cache: 'no-store', signal});
  await requireSessionAcknowledgement(response, 'Your browser blocked or expired the workspace cookie. Open the app directly and allow its cookies.');
  if (signal.aborted) throw new DOMException('Session startup cancelled', 'AbortError');
  clearLegacyCredential();
  ready = true;
}

async function createOrResume(signal: AbortSignal): Promise<void> {
  const response = await rawRequest('/session', {method: 'POST', signal});
  await requireSessionAcknowledgement(response, 'Could not open your workspace. Please refresh.');
}

async function bootstrapSession(signal: AbortSignal): Promise<void> {
  if (ready) return;
  const current = await rawRequest('/session', {cache: 'no-store', signal});
  if (!current.ok && current.status !== 401) throw await responseError(current, 'Could not check your workspace session.');
  if (current.ok) await requireSessionAcknowledgement(current, 'Could not confirm your current workspace session.');
  // Even with a valid cookie, validate any old credential before removing it.
  // A conflicting workspace must never silently replace or strand existing data.
  const restored = await migrateLegacySession(signal, current.ok);
  if (!restored) await createOrResume(signal);
  await confirmCookie(signal);
}

async function serializedBootstrap(signal: AbortSignal): Promise<void> {
  if (navigator.locks?.request) {
    await navigator.locks.request('triageflow-session-bootstrap', {signal}, () => bootstrapSession(signal));
  } else {
    await bootstrapSession(signal);
  }
}

export async function startSession(): Promise<void> {
  if (ready) return;
  if (!starting) starting = withDeadline(serializedBootstrap, {
    timeoutMs: 15000,
    message: 'Connecting to your workspace took too long. Check your connection and try again. Do not clear browser data.',
  }).finally(() => { starting = null; });
  await starting;
}

export function resetSession(): void { ready = false; }